import { t } from '../i18n';
import { appendGreaterBadge } from './skillVariants';

export interface CodexListRow {
  value: string;
  icon: string | HTMLElement;
  title: string;
  idText?: string;
  description?: string;
  stats?: string;
  tags?: string[];
  inBar?: boolean;
  iconBadge?: 'greater';
  onDoubleClick?: (value: string) => void;
}

interface RenderCodexTabOptions {
  query: string;
  onQueryInput: (value: string) => void;
}

export function createCodexRowElement(rowData: CodexListRow): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'skill-list-item';
  if (rowData.onDoubleClick) {
    row.classList.add('codex-pickable-row');
    row.addEventListener('click', () => rowData.onDoubleClick?.(rowData.value));
  }
  if (rowData.inBar) {
    row.classList.add('in-bar');
  }

  const iconBox = document.createElement('div');
  iconBox.className = 'skill-icon-box';
  iconBox.style.position = 'relative';
  if (typeof rowData.icon === 'string') {
    iconBox.textContent = rowData.icon;
  } else {
    iconBox.appendChild(rowData.icon);
  }
  if (rowData.iconBadge === 'greater') {
    appendGreaterBadge(iconBox);
  }

  const infoEl = document.createElement('div');
  infoEl.className = 'skill-list-info';

  const nameEl = document.createElement('div');
  nameEl.className = 'skill-list-name';
  nameEl.textContent = rowData.title;
  if (rowData.idText) {
    const idEl = document.createElement('span');
    idEl.className = 'skill-list-id';
    idEl.textContent = `(${rowData.idText})`;
    nameEl.appendChild(idEl);
  }
  if (rowData.inBar) {
    const badge = document.createElement('span');
    badge.className = 'skill-in-bar-badge';
    badge.textContent = t('character.inBar');
    nameEl.appendChild(badge);
  }
  infoEl.appendChild(nameEl);

  if (rowData.description) {
    const descEl = document.createElement('div');
    descEl.className = 'skill-list-desc';
    descEl.textContent = rowData.description;
    infoEl.appendChild(descEl);
  }

  if (rowData.stats) {
    const statsEl = document.createElement('div');
    statsEl.className = 'skill-list-stats';
    statsEl.textContent = rowData.stats;
    infoEl.appendChild(statsEl);
  }

  if (rowData.tags && rowData.tags.length > 0) {
    const tagsEl = document.createElement('div');
    tagsEl.className = 'skill-list-tags';
    tagsEl.textContent = rowData.tags.join(', ');
    infoEl.appendChild(tagsEl);
  }

  row.appendChild(iconBox);
  row.appendChild(infoEl);
  return row;
}

export function renderCodexTab(
  root: HTMLElement,
  rows: CodexListRow[],
  emptyLabel: string,
  options: RenderCodexTabOptions
): void {
  root.innerHTML = '';
  root.style.display = 'flex';
  root.style.flexDirection = 'column';
  root.style.height = '100%';
  root.style.minHeight = '0';
  root.style.overflow = 'hidden';

  const shell = document.createElement('div');
  shell.className = 'codex-tab-shell';
  shell.style.display = 'flex';
  shell.style.flexDirection = 'column';
  shell.style.flex = '1';
  shell.style.minHeight = '0';

  const searchInput = document.createElement('input');
  searchInput.type = 'search';
  searchInput.className = 'codex-search-input';
  searchInput.placeholder = t('codex.searchPlaceholder');
  searchInput.value = options.query;
  searchInput.addEventListener('input', () => options.onQueryInput(searchInput.value));
  shell.appendChild(searchInput);

  const normalizedQuery = options.query.trim().toLocaleLowerCase();
  const filteredRows = normalizedQuery
    ? rows.filter((row) => row.title.toLocaleLowerCase().includes(normalizedQuery))
    : rows;

  const listHost = document.createElement('div');
  listHost.className = 'codex-list-scroll-root';
  listHost.style.flex = '1';
  listHost.style.minHeight = '0';
  listHost.style.overflowY = 'auto';
  listHost.style.paddingRight = '4px';

  if (filteredRows.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'char-tab-empty';
    empty.textContent = emptyLabel || t('codex.empty');
    listHost.appendChild(empty);
    shell.appendChild(listHost);
    root.appendChild(shell);
    return;
  }

  const list = document.createElement('div');
  list.className = 'skill-list';

  for (const rowData of filteredRows) {
    list.appendChild(createCodexRowElement(rowData));
  }

  listHost.appendChild(list);
  shell.appendChild(listHost);
  root.appendChild(shell);
}
